
  import {mapSectionComponents} from '@/helper/SectionHelper';
  import {getMetaTags} from '@/helper/Seo';
  import LazyHydrate from 'vue-lazy-hydration';
  import {createObjectFromQueryString} from '@/helper/QueryHelper';
  import {scopeHelper} from '@/helper/ScopeHelper';
  import startCase from 'lodash/startCase';
  import axios from 'axios';

  export default {
    name: 'Event',
    components: {
      LazyHydrate,
    },
    async asyncData({route, error, $cachedStrapi, app, redirect, store}) {
      // Query page from Strapi; strapi-change for findOne is required!
      const pagePromise = $cachedStrapi
        .findOne('pages', 'events-category-event')
        .catch((e) => {
          if (e.statusCode !== 404) {
            throw e;
          }
        });

      // Query Event from Strapi based on slug; strapi-change for findOne is required!
      const eventPromise = $cachedStrapi
        .findOne('events', route.params.event)
        .catch(async () => {
          const slug = route.params.event;
          const readableSlug = startCase(slug.replace(/-/g, ' '));
          const currentQuery = route.query;
          const response = await axios.get(process.env.STRAPI_URL + '/events', {
            params: {
              slug,
              _publicationState: 'preview',
            },
          });
          if (response.data.length > 0) {
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'event-deactivated',
              search: readableSlug,
            };

            const redirectRoute = app.localePath({
              name: 'events',
              query: newQueryParam,
            });

            // Redirect the user to the main listing page (with status code 302)
            redirect(301, redirectRoute);
          } else {
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'event-not-found',
              search: readableSlug,
            };

            const redirectRoute = app.localePath({
              name: 'events',
              query: newQueryParam,
            });

            // Redirect the user to the main listing page (with status code 301)
            redirect(302, redirectRoute);
          }
          return;
        });

      const [page, event] = await Promise.all([pagePromise, eventPromise]);

      if (!scopeHelper(page, process.env.SCOPE_CANDIDATE)) {
        error({statusCode: 404, message: 'Page not found'});
        return;
      }
      if (
        !scopeHelper(event, process.env.SCOPE_CANDIDATE) ||
        !event.event_category
      ) {
        error({statusCode: 404, message: 'Event not found'});
        return;
      }

      let search_url = '';
      if (store.getters['breadcrumbs/hasEventListingRoute']) {
        const lastEventListingRoute =
          store.state.breadcrumbs.lastEventListingRoute;
        const filterValues = createObjectFromQueryString(
          lastEventListingRoute.substring(
            lastEventListingRoute.indexOf('?') + 1,
            lastEventListingRoute.lastIndexOf('#')
          )
        );

        //Add filters only if they exist
        let params = '?';
        if (filterValues.search) {
          params = params + 'search=' + filterValues.search;
        }
        if (event.event_category.slug) {
          params =
            params +
            (filterValues.search ? '&' : '') +
            'category[]=' +
            event.event_category.slug;
        }

        search_url =
          app.localePath({
            name: 'events-category',
            params: {
              category: event.event_category.slug,
            },
          }) + params;
      }

      const sections = await mapSectionComponents(
        page.sections,
        async function (section) {
          switch (section.component) {
            case 'SectionSmallHero':
              if (event.logo && event.logo.length > 0) {
                section.image = event.logo[0];
              }
              break;
            case 'SectionEventDetails':
              section.event = event;
              break;

            case 'SectionBreadcrumbs':
              section.breadcrumbs = [
                {
                  text: app.i18n.t('page.index'),
                  disabled: false,
                  to: app.localePath({
                    name: 'index',
                  }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: app.i18n.t('page.events'),
                  disabled: false,
                  to: app.localePath(
                    store.getters['breadcrumbs/hasEventListingRoute']
                      ? store.state.breadcrumbs.lastEventListingRoute.split(
                          '#'
                        )[0]
                      : {
                          name: 'events',
                        }
                  ),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: event.event_category.title,
                  disabled: false,
                  to: store.getters['breadcrumbs/hasEventListingRoute']
                    ? search_url.split('#')[0]
                    : app.localePath({
                        name: 'events-category',
                        params: {
                          category: event.event_category.slug,
                        },
                      }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: event.title,
                  disabled: true,
                  to: app.localePath({
                    name: 'events-category-event',
                    params: {
                      category: event.event_category.slug,
                      event: event.slug,
                    },
                  }),
                  nuxt: true,
                  exact: true,
                },
              ];
              break;

            case 'SectionSeoContent':
              if (
                event.seo_content_config &&
                event.seo_content_config.seo_contents &&
                event.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  event.seo_content_config.seo_contents[0].id
                );
              } else if (
                section.seo_content_config &&
                section.seo_content_config.seo_contents &&
                section.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  section.seo_content_config.seo_contents[0].id
                );
              }
              break;
          }
        }
      );

      // Meta-Data mit Werten vom Artikel
      const metaData =
        event && event.meta ? event.meta : page && page.meta ? page.meta : {};

      return {
        metaData,
        sections,
      };
    },
    data() {
      return {
        metaData: {},
        sections: [],
      };
    },
    head() {
      return {
        title: this.metaData.title,
        meta: getMetaTags(this.metaData),
      };
    },
  };
